/**
 * WarrChirp Keyboard Shortcuts
 *
 * Mirrors WarrQueue design principles:
 * - Ctrl+ for primary actions (Save, etc.)
 * - Ctrl+Shift+ for deliberate actions (Reassign, Export, Clear filters)
 * - Alt+ for secondary actions (Column picker, New ticket, etc.)
 * - F-keys for modal dialogs where applicable
 * - Same hotkeys for same button functions as WarrQueue
 * - No reserved browser shortcuts (Ctrl+T, Ctrl+W, Ctrl+N, F5, F11, F12, etc.)
 */

export interface ShortcutDescriptor {
  combo: string;
  description: string;
  allowInInputs?: boolean;
  action?: string;
}

export interface ShortcutContextMap {
  title: string;
  shortcuts: ShortcutDescriptor[];
}

const ticketsShortcuts: ShortcutDescriptor[] = [
  // ====================
  // PRIMARY ACTIONS (Ctrl+)
  // ====================
  { combo: 'Ctrl+S', description: 'Save ticket changes', allowInInputs: true, action: 'save' },
  {
    combo: 'Escape',
    description: 'Close modal or cancel edit',
    allowInInputs: true,
    action: 'cancelEdit',
  },

  // ====================
  // HEADER ACTIONS (same as WarrQueue where applicable)
  // ====================
  {
    combo: 'Ctrl+F',
    description: 'Open filters',
    allowInInputs: true,
    action: 'openFilters',
  },
  {
    combo: 'Ctrl+Shift+F',
    description: 'Clear all filters',
    allowInInputs: true,
    action: 'clearFilters',
  },
  {
    combo: 'Alt+P',
    description: 'Open column picker',
    allowInInputs: true,
    action: 'openColumnPicker',
  },
  {
    combo: 'Alt+E',
    description: 'Edit selected tickets',
    allowInInputs: true,
    action: 'editSelectedTickets',
  },
  {
    combo: 'Ctrl+Shift+E',
    description: 'Export tickets to CSV',
    allowInInputs: true,
    action: 'exportTickets',
  },
  {
    combo: 'Ctrl+Shift+R',
    description: 'Reassign selected tickets',
    allowInInputs: true,
    action: 'reassign',
  },
  {
    combo: 'F3',
    description: 'New ticket',
    allowInInputs: true,
    action: 'newTicket',
  },
  {
    combo: 'Ctrl+X',
    description: 'Cancel edit mode',
    allowInInputs: true,
    action: 'cancelEdit',
  },
  {
    combo: 'Alt+H',
    description: 'Share selected tickets',
    allowInInputs: true,
    action: 'share',
  },
  {
    combo: 'Alt+Z',
    description: 'Close selected tickets',
    allowInInputs: true,
    action: 'closeTickets',
  },
  {
    combo: 'Alt+I',
    description: 'Import schedule',
    allowInInputs: true,
    action: 'importSchedule',
  },
  {
    combo: 'Alt+L',
    description: 'View imported schedule list',
    allowInInputs: true,
    action: 'viewScheduleFiles',
  },
];

/**
 * Ticket details view shortcuts (when viewing a single ticket).
 * Same hotkeys as WarrQueue work item where applicable.
 */
const ticketDetailsShortcuts: ShortcutDescriptor[] = [
  { combo: 'Ctrl+S', description: 'Save ticket details', allowInInputs: true, action: 'save' },
  {
    combo: 'Escape',
    description: 'Close modal or close tab',
    allowInInputs: true,
    action: 'closeTicketDetails',
  },
  {
    combo: 'Ctrl+Shift+R',
    description: 'Reassign ticket',
    allowInInputs: true,
    action: 'reassign',
  },
  {
    combo: 'Alt+H',
    description: 'Share ticket',
    allowInInputs: true,
    action: 'share',
  },
  {
    combo: 'Alt+A',
    description: 'Add attachment',
    allowInInputs: true,
    action: 'addAttachment',
  },
  {
    combo: 'Alt+B',
    description: 'Get approval',
    allowInInputs: true,
    action: 'getApproval',
  },
  {
    combo: 'Alt+Z',
    description: 'Close or reopen ticket',
    allowInInputs: true,
    action: 'closeOrReopenTicket',
  },
];

export const ticketDetailsShortcutMap: ShortcutContextMap = {
  title: 'Ticket Details Shortcuts',
  shortcuts: ticketDetailsShortcuts,
};

export const ticketsShortcutMap: ShortcutContextMap = {
  title: 'WarrChirp Shortcuts',
  shortcuts: ticketsShortcuts,
};

export const getAllShortcutDescriptions = (): {
  title: string;
  shortcuts: readonly ShortcutDescriptor[];
}[] => {
  return [
    { title: ticketsShortcutMap.title, shortcuts: ticketsShortcutMap.shortcuts },
    { title: ticketDetailsShortcutMap.title, shortcuts: ticketDetailsShortcutMap.shortcuts },
  ];
};
